import SockJS from "sockjs-client";
import { Client } from "@stomp/stompjs";
import { getToken } from "functions/Auth";
import { detectBrowserAndDevice } from "Common/Functions";

let client = null;
let connected = false;
let isActivating = false;

let subscriptions = new Map(); // topic -> callback
let publishQueue = []; // queued messages

export const stompService = {
  /**
   * Connect WebSocket
   */
  connect: (url, onConnect, onError) => {
    if (client?.active || isActivating) {
      return client;
    }

    isActivating = true;

    const token = getToken();
    const { deviceId } = detectBrowserAndDevice();

    client = new Client({
      webSocketFactory: () =>
        new SockJS(`${url}?token=${token}&deviceId=${deviceId}`),

      connectHeaders: {
        Authorization: `Bearer ${token}`,
        deviceId: deviceId,
      },

      reconnectDelay: 5000,

      heartbeatIncoming: 4000,
      heartbeatOutgoing: 4000,

      debug: () => {},

      /**
       * Connected
       */
      onConnect: (frame) => {
        connected = true;
        isActivating = false;

        console.log("✅ STOMP connected");

        /**
         * Resubscribe queued topics
         */
        subscriptions.forEach((callback, topic) => {
          client.subscribe(topic, callback);
          console.log(`📡 Subscribed to ${topic}`);
        });

        /**
         * Flush publish queue
         */
        while (publishQueue.length > 0) {
          const msg = publishQueue.shift();
          client.publish(msg);
        }

        onConnect?.(frame);
      },

      /**
       * Websocket Error
       */
      onStompError: (frame) => {
        console.error("❌ Broker error:", frame.headers["message"]);
        console.error(frame.body);
        onError?.(frame);
      },

      /**
       * Disconnect
       */
      onWebSocketClose: () => {
        console.warn("🔌 WebSocket closed");
        connected = false;
      },
    });

    client.activate();

    return client;
  },

  /**
   * Subscribe to topic
   */
  subscribe: (topic, callback) => {
    if (subscriptions.has(topic)) {
      console.warn(`⚠️ Already subscribed to ${topic}`);
      return;
    }

    const wrappedCallback = (message) => {
      try {
        callback(JSON.parse(message.body), message);
      } catch {
        callback(message.body, message);
      }
    };

    /**
     * Save in queue
     */
    subscriptions.set(topic, wrappedCallback);

    /**
     * If already connected subscribe immediately
     */
    let subscription = null;

    if (client && connected) {
      subscription = client.subscribe(topic, wrappedCallback);
      console.log(`📡 Subscribed instantly to ${topic}`);
    } else {
      console.log(`📌 Queued subscription for ${topic}`);
    }

    /**
     * Unsubscribe handler
     */
    return () => {
      if (subscription) {
        subscription.unsubscribe();
      }

      subscriptions.delete(topic);

      console.log(`🧹 Unsubscribed from ${topic}`);
    };
  },

  /**
   * Publish message
   */
  send: (destination, body = {}) => {
    const message = {
      destination,
      body: typeof body === "string" ? body : JSON.stringify(body),
    };

    /**
     * Send immediately if connected
     */
    if (client && connected) {
      client.publish(message);
    } else {
      /**
       * Queue message
       */
      publishQueue.push(message);
      console.log("📌 Message queued until connection");
    }
  },

  /**
   * Disconnect
   */
  disconnect: () => {
    if (client) {
      client.deactivate();

      client = null;
      connected = false;
      isActivating = false;

      subscriptions.clear();
      publishQueue = [];

      console.log("🔌 STOMP disconnected");
    }
  },

  /**
   * Connection state
   */
  isConnected: () => connected,
};